import React, { useState, useEffect, useRef } from 'react';
import {
    X,
    Shield,
    ShieldAlert,
    ShieldCheck,
    AlertTriangle,
    CheckCircle,
    CheckCircle2,
    XCircle,
    Clock,
    CreditCard,
    Calendar,
    Activity,
    AlertCircle,
    Loader2,
    RefreshCw,
    Bot
} from 'lucide-react';
import { apiClient, LicenceCheckResult, Driver, ClearToDriveBlocker, DriverIssue, FleetSubtype } from '../../services/api';

interface LicenceCheckModalProps {
    isOpen: boolean;
    onClose: () => void;
    driver: Driver | null;
    onCheckComplete?: () => void;
    subtype?: FleetSubtype;
}

const LicenceCheckModal: React.FC<LicenceCheckModalProps> = ({
    isOpen,
    onClose,
    driver,
    onCheckComplete,
    subtype = 'general'
}) => {
    const [isChecking, setIsChecking] = useState(false);
    const isCheckingRef = useRef(false);
    const [checkResult, setCheckResult] = useState<LicenceCheckResult | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [isLoadingLatest, setIsLoadingLatest] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [checkHistory, setCheckHistory] = useState<LicenceCheckResult[]>([]);
    const [isLoadingHistory, setIsLoadingHistory] = useState(false);
    // Clear to drive status from API (pre-computed on backend)
    const [clearToDriveData, setClearToDriveData] = useState<{
        clearToDrive?: boolean;
        issues?: DriverIssue[];
        clearToDriveBlockers?: ClearToDriveBlocker[];
    } | null>(null);
    const [showRecentCheckConfirm, setShowRecentCheckConfirm] = useState(false);

    // Load latest check when modal opens
    useEffect(() => {
        if (isOpen && driver) {
            loadLatestCheck();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, driver]);

    const loadLatestCheck = async () => {
        if (!driver) return;

        try {
            setIsLoadingLatest(true);
            setError(null);

            const result = await apiClient.getLatestLicenceCheck(driver.id);

            // Store clearToDrive data from API response
            if (result.data.driver) {
                setClearToDriveData({
                    clearToDrive: result.data.driver.clearToDrive,
                    issues: result.data.driver.issues,
                    clearToDriveBlockers: result.data.driver.clearToDriveBlockers
                });
            }

            if (result.data.latestCheck) {
                setCheckResult(result.data.latestCheck);
            }
        } catch (err: any) {
            // If no previous check exists, that's not an error - just means first check
            if (!err.message?.includes('not found')) {
                console.error('Error loading latest check:', err);
            }
            // Fallback to driver prop data if API call fails
            setClearToDriveData({
                clearToDrive: driver.clearToDrive,
                issues: driver.issues,
                clearToDriveBlockers: driver.issues?.filter(i => i.severity === 'critical').map(i => ({
                    code: i.code,
                    label: i.label,
                    description: i.filterLabel || ''
                }))
            });
        } finally {
            setIsLoadingLatest(false);
        }
    };

    const loadCheckHistory = async () => {
        if (!driver) return;

        try {
            setIsLoadingHistory(true);

            const { apiClient } = await import('../../services/api');
            const result = await apiClient.getLicenceCheckHistory(driver.id);

            setCheckHistory(result.data || []);
        } catch (err: any) {
            console.error('Error loading check history:', err);
        } finally {
            setIsLoadingHistory(false);
        }
    };

    // Check if consent has expired
    const isConsentExpired = () => {
        if (!driver || !driver.consentProvided || !driver.consentExpiry) return false;
        return new Date(driver.consentExpiry) <= new Date();
    };

    const isRecentCheck = () =>
        !!(driver?.lastCheckDate &&
           driver.lastCheckWasSuccessful === true &&
           driver.daysSinceLastCheck !== null &&
           driver.daysSinceLastCheck !== undefined &&
           driver.daysSinceLastCheck <= 7);

    const handleCheckButtonClick = () => {
        if (!driver) return;

        if (!driver.consentProvided) {
            setError('Driver consent is required before performing licence checks. Please record D906 consent first.');
            return;
        }

        if (isConsentExpired()) {
            setError('Driver consent has expired. Please renew D906 consent before performing licence checks.');
            return;
        }

        if (isRecentCheck()) {
            setShowRecentCheckConfirm(true);
            return;
        }

        performLicenceCheck();
    };

    const performLicenceCheck = async () => {
        if (!driver) return;
        if (isCheckingRef.current) return;

        setShowRecentCheckConfirm(false);

        try {
            isCheckingRef.current = true;
            setIsChecking(true);
            setError(null);

            const result = await apiClient.performLicenceCheck(driver.id);

            setCheckResult(result.data);

            // Refresh clearToDriveData to show updated issues/status immediately
            await loadLatestCheck();

            if (onCheckComplete) {
                onCheckComplete();
            }

        } catch (err: any) {
            setError(err.message || 'Failed to perform licence check');
            console.error('Error performing licence check:', err);
        } finally {
            isCheckingRef.current = false;
            setIsChecking(false);
        }
    };

    const getRiskBadge = (level: string) => {
        const getRiskStyles = (risk: string) => {
            switch (risk.toLowerCase()) {
                case 'low':
                    return 'bg-green-100 text-green-800 border-green-200';
                case 'medium':
                    return 'bg-yellow-100 text-yellow-800 border-yellow-200';
                case 'high':
                    return 'bg-red-100 text-red-800 border-red-200';
                default:
                    return 'bg-gray-100 text-gray-800 border-gray-200';
            }
        };

        const getRiskIcon = (risk: string) => {
            switch (risk.toLowerCase()) {
                case 'low':
                    return <ShieldCheck className="w-3.5 h-3.5" />;
                case 'medium':
                    return <Shield className="w-3.5 h-3.5" />;
                case 'high':
                    return <ShieldAlert className="w-3.5 h-3.5" />;
                default:
                    return <Shield className="w-3.5 h-3.5" />;
            }
        };

        return (
            <div className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full border text-sm ${getRiskStyles(level)}`}>
                {getRiskIcon(level)}
                <span className="font-medium capitalize">{level} Risk</span>
            </div>
        );
    };

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusBadge = (status: string, valid: boolean) => {
        const statusLower = status?.toLowerCase() || '';

        // Check for invalid/negative statuses - these should always show red
        const isInvalid = !valid ||
            statusLower.includes('expired') ||
            statusLower.includes('revoked') ||
            statusLower.includes('disqualified') ||
            statusLower.includes('invalid');

        if (isInvalid) {
            return (
                <div className="inline-flex items-center space-x-1.5 px-3 py-1 rounded-full bg-red-100 text-red-800 border border-red-200 text-sm">
                    <AlertCircle className="w-3.5 h-3.5" />
                    <span className="font-medium">{status || 'Invalid'}</span>
                </div>
            );
        }

        const isProvisional = statusLower.includes('provisional');

        return (
            <div className={`inline-flex items-center space-x-1.5 px-3 py-1 rounded-full border text-sm ${isProvisional
                ? 'bg-yellow-100 text-yellow-800 border-yellow-200'
                : 'bg-green-100 text-green-800 border-green-200'
                }`}>
                <CheckCircle className="w-3.5 h-3.5" />
                <span className="font-medium">{status}</span>
            </div>
        );
    };

    if (!isOpen || !driver) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="p-4 sm:p-6 border-b border-gray-200">
                    {/* Top row: Title and close button */}
                    <div className="flex items-start justify-between">
                        <div className="flex items-start space-x-3 min-w-0 flex-1">
                            <div className="p-2 rounded-lg flex-shrink-0" style={{ backgroundColor: 'rgba(105, 190, 40, 0.1)' }}>
                                <Shield className="w-5 h-5 sm:w-6 sm:h-6" style={{ color: '#69BE28' }} />
                            </div>
                            <div className="min-w-0 flex-1">
                                <h2 className="text-lg sm:text-xl font-bold text-gray-900 truncate">DVLA Licence Check</h2>
                                <div className="space-y-1 mt-1">
                                    <p className="text-sm font-medium text-gray-700 truncate">
                                        {driver.firstName} {driver.lastName}
                                    </p>
                                    <p className="text-xs sm:text-sm text-gray-500 font-mono truncate">
                                        {driver.licenceNumber}
                                    </p>
                                </div>
                            </div>
                        </div>
                        <button
                            onClick={onClose}
                            disabled={isChecking}
                            className="text-gray-400 hover:text-gray-600 disabled:opacity-50 p-1 ml-2 flex-shrink-0"
                        >
                            <X className="w-5 h-5 sm:w-6 sm:h-6" />
                        </button>
                    </div>

                    {/* Actions row: Last checked and Perform New Check button */}
                    <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        {checkResult ? (
                            <div className="flex items-center space-x-2 text-sm text-gray-500">
                                <Clock className="w-4 h-4 flex-shrink-0" />
                                <span>Last checked: {formatDate(checkResult.checkDate)}</span>
                            </div>
                        ) : (
                            <div className="text-sm text-gray-500">No previous check</div>
                        )}
                        <button
                            onClick={handleCheckButtonClick}
                            disabled={isChecking || !driver.consentProvided || isConsentExpired()}
                            className="inline-flex items-center justify-center px-4 py-2 text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed space-x-2 text-sm"
                            style={{ backgroundColor: '#69BE28' }}
                        >
                            {isChecking ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    <span>Checking DVLA...</span>
                                </>
                            ) : (
                                <>
                                    <RefreshCw className="w-4 h-4" />
                                    <span>Perform New Check</span>
                                </>
                            )}
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Clear to Drive Status - shown only when no check result exists */}
                    {isLoadingLatest ? (
                        <div className="h-12 bg-gray-100 rounded-lg animate-pulse" />
                    ) : checkResult === null && !isLoadingLatest ? (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg px-4 py-3 flex items-center gap-2">
                            <ShieldAlert className="w-5 h-5 text-yellow-600 flex-shrink-0" />
                            <span className="font-medium text-yellow-800">
                                Clear to drive status unknown - no licence check on record
                            </span>
                        </div>
                    ) : null}

                    {/* Consent Check */}
                    {!driver.consentProvided && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center space-x-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                <div>
                                    <h3 className="font-medium text-yellow-800">Consent Required</h3>
                                    <p className="text-sm text-yellow-800">
                                        D906 consent must be recorded before performing licence checks.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Expired Consent Check */}
                    {driver.consentProvided && isConsentExpired() && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                            <div className="flex items-center space-x-2">
                                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                                <div>
                                    <h3 className="font-medium text-yellow-800">Consent Required</h3>
                                    <p className="text-sm text-yellow-800">
                                        D906 consent must be recorded before performing licence checks.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Error Display */}
                    {error && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                            <div className="flex items-center space-x-2">
                                <AlertCircle className="w-5 h-5 text-red-500" />
                                <span className="text-red-800">{error}</span>
                            </div>
                        </div>
                    )}

                    {/* Loading Latest Check */}
                    {isLoadingLatest && (
                        <div className="text-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                            <p className="mt-2 text-gray-600">Loading latest check result...</p>
                        </div>
                    )}

                    {/* Check Results */}
                    {checkResult && (
                        <div className="space-y-6">
                            {/* Licence Status Overview - with integrated Clear to Drive header */}
                            <div className="rounded-lg overflow-hidden border border-gray-200">
                                {/* Clear to Drive Header - compact banner, expandable for details */}
                                {(() => {
                                    // Consolidated action map for all issue types (DRY)
                                    const issueActionMap: Record<string, string> = {
                                        // Critical blockers
                                        licence_invalid: 'Driver must resolve licence status with DVLA before driving professionally.',
                                        licence_expired: 'Driver must renew their licence with DVLA immediately.',
                                        disqualified: 'Driver cannot drive until disqualification period ends and licence is reinstated.',
                                        cpc_expired: 'Driver must complete 35 hours of periodic CPC training to regain qualification.',
                                        missing_bus_licence: 'Driver needs to pass D/D1 test to legally drive PCV vehicles.',
                                        missing_d_licence: 'Driver needs to pass D/D1 test to legally drive PCV vehicles.',
                                        minibus_only: 'Driver only has D1 (minibus ≤16 passengers). Cannot drive full buses - requires Category D licence.',
                                        restricted_hire_reward: 'Restriction 101 prevents driving passengers for hire. Driver needs full D entitlement.',
                                        missing_hgv_licence: 'Driver needs to pass C/CE test to legally drive HGV vehicles.',
                                        missing_c_licence: 'Driver needs to pass C/CE test to legally drive HGV vehicles.',
                                        tacho_expired: 'Driver must apply for a new digital tachograph card from DVLA.',
                                        check_failed: 'Unable to verify licence status. Check driver details are correct and try again.',
                                        pcv_category_expired: 'D/D1 entitlement has expired. Driver must renew before driving PCV vehicles.',
                                        hgv_category_expired: 'C/CE entitlement has expired. Driver must renew before driving HGV vehicles.',
                                        pcv_category_provisional: 'D/D1 is provisional only. Driver must pass full test for professional PCV driving.',
                                        hgv_category_provisional: 'C/CE is provisional only. Driver must pass full test for professional HGV driving.',
                                        // Warnings
                                        licence_expiring: 'Schedule licence renewal before expiry date.',
                                        cpc_expiring: 'Schedule CPC training before expiry to maintain driving eligibility.',
                                        tacho_expiring: 'Apply for new digital tachograph card before current one expires.',
                                        pcv_category_expiring: 'D/D1 category expiring - schedule D4 medical for renewal.',
                                        hgv_category_expiring: 'C/CE category expiring - schedule D4 medical for renewal.',
                                        car_category_expiring: 'Category B expiring - schedule licence renewal.',
                                    };

                                    // Helper to render expandable issue banner (DRY)
                                    const renderIssueBanner = (
                                        issues: Array<{ code: string; label: string; filterLabel?: string; description?: string }>,
                                        config: {
                                            title: string;
                                            headerBg: string;
                                            headerHover: string;
                                            contentBg: string;
                                            borderColor: string;
                                            badgeBg: string;
                                            badgeText: string;
                                            badgeBorder: string;
                                            tooltipLabelColor: string;
                                            countLabel: string;
                                            Icon: React.ComponentType<{ className?: string }>;
                                            BadgeIcon: React.ComponentType<{ className?: string }>;
                                            HeaderIcon: React.ComponentType<{ className?: string }>;
                                            defaultAction: string;
                                        }
                                    ) => (
                                        <details>
                                            <summary className={`${config.headerBg} text-white px-4 py-2.5 cursor-pointer list-none flex items-center justify-between ${config.headerHover} transition-colors`}>
                                                <div className="flex items-center gap-2">
                                                    <svg className="w-4 h-4 text-white/70 transition-transform [details[open]_&]:rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                    </svg>
                                                    <config.HeaderIcon className="w-5 h-5 flex-shrink-0" />
                                                    <span className="font-medium">{config.title}</span>
                                                </div>
                                                <span className="text-sm opacity-90">
                                                    {issues.length} {config.countLabel}{issues.length !== 1 ? 's' : ''}
                                                </span>
                                            </summary>
                                            <div className={`${config.contentBg} px-4 py-3 border-t ${config.borderColor}`}>
                                                <div className="flex flex-wrap gap-1.5">
                                                    {issues.map((issue, i) => {
                                                        const action = issueActionMap[issue.code] || issue.description || config.defaultAction;
                                                        const displayLabel = issue.filterLabel || issue.label;
                                                        return (
                                                            <div key={i} className="group relative">
                                                                <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-sm font-medium ${config.badgeBg} ${config.badgeText} border ${config.badgeBorder} cursor-help`}>
                                                                    <config.BadgeIcon className="w-4 h-4 flex-shrink-0" />
                                                                    {displayLabel}
                                                                </span>
                                                                <div className="absolute left-0 top-full mt-2 hidden group-hover:block p-2.5 bg-gray-900 text-white text-xs rounded shadow-lg z-30 pointer-events-none w-64">
                                                                    <div className="absolute left-4 bottom-full w-0 h-0 border-l-4 border-r-4 border-b-4 border-transparent border-b-gray-900"></div>
                                                                    <div className={`font-medium ${config.tooltipLabelColor} mb-1`}>{displayLabel}</div>
                                                                    {issue.description && <div className="text-gray-300 mb-2">{issue.description}</div>}
                                                                    <div className="pt-2 border-t border-gray-700">
                                                                        <div className="text-yellow-300 font-medium text-[10px] uppercase tracking-wide mb-0.5">Recommended Action</div>
                                                                        <div className="text-gray-200">{action}</div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </details>
                                    );

                                    const warningIssues = clearToDriveData?.issues?.filter(i => i.severity === 'warning') || [];
                                    const blockers = clearToDriveData?.clearToDriveBlockers || [];

                                    if (clearToDriveData?.clearToDrive && warningIssues.length > 0) {
                                        return renderIssueBanner(warningIssues, {
                                            title: 'Clear to drive (warnings only)',
                                            headerBg: 'bg-yellow-500',
                                            headerHover: 'hover:bg-yellow-600',
                                            contentBg: 'bg-yellow-50',
                                            borderColor: 'border-yellow-200',
                                            badgeBg: 'bg-yellow-100',
                                            badgeText: 'text-yellow-800',
                                            badgeBorder: 'border-yellow-200',
                                            tooltipLabelColor: 'text-yellow-300',
                                            countLabel: 'warning',
                                            HeaderIcon: ShieldCheck,
                                            BadgeIcon: AlertTriangle,
                                            Icon: ShieldCheck,
                                            defaultAction: 'Review and address this warning before it becomes critical.',
                                        });
                                    } else if (clearToDriveData?.clearToDrive) {
                                        return (
                                            <div className="bg-green-600 text-white px-4 py-2.5 flex items-center gap-2">
                                                <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                                                <span className="font-medium">Clear to drive</span>
                                            </div>
                                        );
                                    } else if (blockers.length > 0) {
                                        return renderIssueBanner(blockers, {
                                            title: 'Not clear to drive',
                                            headerBg: 'bg-red-600',
                                            headerHover: 'hover:bg-red-700',
                                            contentBg: 'bg-red-50',
                                            borderColor: 'border-red-200',
                                            badgeBg: 'bg-red-100',
                                            badgeText: 'text-red-800',
                                            badgeBorder: 'border-red-200',
                                            tooltipLabelColor: 'text-red-300',
                                            countLabel: 'issue',
                                            HeaderIcon: ShieldAlert,
                                            BadgeIcon: AlertCircle,
                                            Icon: ShieldAlert,
                                            defaultAction: 'Contact the driver to resolve this issue before they can drive.',
                                        });
                                    } else if (clearToDriveData) {
                                        return (
                                            <div className="bg-red-600 text-white px-4 py-2.5 flex items-center gap-2">
                                                <ShieldAlert className="w-5 h-5 flex-shrink-0" />
                                                <span className="font-medium">Not clear to drive</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                })()}

                                {/* Card Content - white background */}
                                <div className="bg-white p-4 sm:p-6">
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <CreditCard className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-600 flex-shrink-0" />
                                        Licence Status
                                    </h3>

                                    {/* Status Row - 3 columns */}
                                <div className="grid grid-cols-3 gap-4 mb-4">
                                    <div>
                                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Validity</label>
                                        <div>
                                            {getStatusBadge(checkResult.status, checkResult.valid)}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Licence Type</label>
                                        <div className="text-sm font-medium text-gray-900">
                                            {checkResult.licenceType || 'Not specified'}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-1">Penalty Points</label>
                                        <div>
                                            {(checkResult.status?.toLowerCase().includes('disqualified') ||
                                              checkResult.status?.toLowerCase().includes('revoked')) ? (
                                                <span className="text-xl sm:text-2xl font-bold text-red-600">
                                                    N/A
                                                </span>
                                            ) : (() => {
                                                const pts = (checkResult.endorsements || [])
                                                    .filter((e: any) => !e.penaltyPointsExpiryDate || new Date(e.penaltyPointsExpiryDate) > new Date())
                                                    .reduce((sum: number, e: any) => sum + (e.penaltyPoints || 0), 0);
                                                return (
                                                    <span className={`text-xl sm:text-2xl font-bold ${pts >= 9 ? 'text-red-600' : pts >= 6 ? 'text-yellow-600' : 'text-green-600'}`}>
                                                        {pts}
                                                    </span>
                                                );
                                            })()}
                                        </div>
                                    </div>
                                </div>

                                {/* Categories Row - Horizontal layout */}
                                <div className="pt-4 border-t border-gray-100">
                                    <label className="block text-xs sm:text-sm font-medium text-gray-500 mb-2">Categories</label>
                                    {checkResult.categories.length > 0 ? (
                                        (() => {
                                            const now = new Date();
                                            const pcvCodes = ['D', 'D1', 'DE', 'D1E'];
                                            const hgvCodes = ['C', 'C1', 'CE', 'C1E'];
                                            const professionalCodes = [...pcvCodes, ...hgvCodes];

                                            // Sort categories
                                            const sortedCategories = [...checkResult.categories].sort((a, b) => {
                                                const getPriority = (cat: typeof a) => {
                                                    const isPcv = pcvCodes.includes(cat.categoryCode);
                                                    const isHgv = hgvCodes.includes(cat.categoryCode);
                                                    if (subtype === 'bus' || subtype === 'coach') {
                                                        if (isPcv) return 0;
                                                        if (isHgv) return 1;
                                                        return 2;
                                                    } else if (subtype === 'haulage') {
                                                        if (isHgv) return 0;
                                                        if (isPcv) return 1;
                                                        return 2;
                                                    } else {
                                                        if (isPcv || isHgv) return 0;
                                                        return 1;
                                                    }
                                                };
                                                const priorityDiff = getPriority(a) - getPriority(b);
                                                if (priorityDiff !== 0) return priorityDiff;

                                                const getSeverity = (cat: typeof a) => {
                                                    const isExpired = cat.expiryDate && new Date(cat.expiryDate) <= now;
                                                    const isProvisional = cat.categoryType?.toLowerCase() === 'provisional';
                                                    const hasRestrictions = cat.restrictions && cat.restrictions.length > 0;
                                                    if (isExpired) return 0;
                                                    if (hasRestrictions) return 1;
                                                    if (isProvisional) return 2;
                                                    return 3;
                                                };
                                                return getSeverity(a) - getSeverity(b);
                                            });

                                            // Split into professional and standard based on subtype
                                            // Bus/Coach: Show PCV (D family) as professional
                                            // Haulage: Show HGV (C family) as professional
                                            // Car Fleet: No professional categories (B is standard)
                                            // General: Show all professional categories
                                            const getRelevantProfessionalCodes = () => {
                                                if (subtype === 'bus' || subtype === 'coach') return pcvCodes;
                                                if (subtype === 'haulage') return hgvCodes;
                                                if (subtype === 'car_fleet') return []; // No professional categories for car fleet
                                                return professionalCodes; // General: show all
                                            };
                                            const relevantProfCodes = getRelevantProfessionalCodes();
                                            const professionalCats = sortedCategories.filter(c => relevantProfCodes.includes(c.categoryCode));
                                            const standardCats = sortedCategories.filter(c => !relevantProfCodes.includes(c.categoryCode));

                                            // Subtype-specific label for professional categories
                                            const getProfessionalLabel = () => {
                                                if (subtype === 'bus' || subtype === 'coach') return 'PCV';
                                                if (subtype === 'haulage') return 'HGV';
                                                return 'Professional';
                                            };

                                            // Category name mappings
                                            const categoryNames: Record<string, string> = {
                                                'C': 'HGV (Rigid)', 'C1': 'Medium HGV', 'CE': 'HGV + Trailer', 'C1E': 'Medium HGV + Trailer',
                                                'D': 'Bus/Coach', 'D1': 'Minibus', 'DE': 'Bus + Trailer', 'D1E': 'Minibus + Trailer',
                                                'B': 'Car', 'BE': 'Car + Trailer', 'B1': 'Light vehicle',
                                                'A': 'Motorcycle', 'A1': 'Light motorcycle', 'A2': 'Medium motorcycle', 'AM': 'Moped',
                                                'F': 'Tractor', 'G': 'Road roller', 'H': 'Track vehicle', 'K': 'Mowing machine',
                                                'L': 'Electric vehicle', 'N': 'Exempt vehicle', 'P': 'Moped', 'Q': 'Motor vehicle'
                                            };

                                            const renderCategoryBadge = (cat: any, idx: number, isProfessional: boolean) => {
                                                const expiryDateStr = cat.validToDate || cat.expiryDate;
                                                const expiryDate = expiryDateStr ? new Date(expiryDateStr) : null;
                                                const isExpired = expiryDate && expiryDate <= now;
                                                const warningDate = new Date(now.getTime() + (30 * 24 * 60 * 60 * 1000)); // 30 days warning
                                                const isExpiringSoon = expiryDate && !isExpired && expiryDate <= warningDate;
                                                const isProvisional = cat.categoryType?.toLowerCase() === 'provisional';
                                                const hasRestrictions = cat.restrictions && cat.restrictions.length > 0;

                                                let bgColor, textColor, borderColor, statusDot, statusText = '';

                                                if (isExpired) {
                                                    bgColor = 'bg-red-50';
                                                    textColor = 'text-red-700';
                                                    borderColor = 'border-red-200';
                                                    statusDot = <span className="w-1.5 h-1.5 rounded-full bg-red-500"></span>;
                                                    statusText = 'Expired';
                                                } else if (isProvisional) {
                                                    bgColor = 'bg-yellow-50';
                                                    textColor = 'text-yellow-700';
                                                    borderColor = 'border-yellow-200';
                                                    statusDot = <span className="w-1.5 h-1.5 rounded-full bg-yellow-500"></span>;
                                                    statusText = 'Provisional';
                                                } else if (hasRestrictions) {
                                                    bgColor = 'bg-orange-50';
                                                    textColor = 'text-orange-700';
                                                    borderColor = 'border-orange-200';
                                                    statusDot = <span className="w-1.5 h-1.5 rounded-full bg-orange-500"></span>;
                                                    statusText = 'Restricted';
                                                } else if (isProfessional) {
                                                    bgColor = 'bg-green-50';
                                                    textColor = 'text-green-700';
                                                    borderColor = 'border-green-200';
                                                    statusDot = <span className="w-1.5 h-1.5 rounded-full bg-green-500"></span>;
                                                    statusText = 'Full';
                                                } else {
                                                    // Standard categories - muted style
                                                    bgColor = 'bg-gray-50';
                                                    textColor = 'text-gray-500';
                                                    borderColor = 'border-gray-200';
                                                    statusDot = null;
                                                }

                                                // Format expiry date for tooltip
                                                const formatExpiryDate = (date: Date | null) => {
                                                    if (!date) return null;
                                                    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
                                                };

                                                const expiryColorClass = isExpired ? 'text-red-400' : isExpiringSoon ? 'text-yellow-400' : 'text-green-400';

                                                const tooltipContent = (
                                                    <div className="text-left">
                                                        <div className="font-medium text-center">{categoryNames[cat.categoryCode] || cat.categoryCode}</div>
                                                        {statusText && (
                                                            <div className={`text-xs mt-1 text-center ${isExpired ? 'text-red-300' : isProvisional ? 'text-yellow-300' : hasRestrictions ? 'text-orange-300' : 'text-green-300'}`}>
                                                                {statusText}
                                                            </div>
                                                        )}
                                                        {expiryDate && (
                                                            <div className={`text-xs mt-1 text-center ${expiryColorClass}`}>
                                                                {isExpired ? 'Expired: ' : isExpiringSoon ? 'Expires: ' : 'Valid until: '}
                                                                {formatExpiryDate(expiryDate)}
                                                            </div>
                                                        )}
                                                        {hasRestrictions && cat.restrictions && cat.restrictions.length > 0 && (
                                                            <div className="mt-2 pt-2 border-t border-gray-700 space-y-1">
                                                                {cat.restrictions.map((r: any, i: number) => (
                                                                    <div key={i} className="text-orange-300">
                                                                        <span className="font-medium">{r.restrictionCode}</span>
                                                                        {r.restrictionLiteral && <span className="text-gray-300"> - {r.restrictionLiteral}</span>}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        )}
                                                    </div>
                                                );

                                                return (
                                                    <div key={idx} className="group relative">
                                                        <span
                                                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border cursor-default ${bgColor} ${textColor} ${borderColor}`}
                                                        >
                                                            {statusDot}
                                                            {cat.categoryCode}
                                                        </span>
                                                        <div className={`absolute left-1/2 -translate-x-1/2 bottom-full mb-2 hidden group-hover:block p-2 bg-gray-900 text-white text-xs rounded shadow-lg z-20 pointer-events-none ${hasRestrictions ? 'w-56' : 'w-36'}`}>
                                                            {tooltipContent}
                                                            <div className="absolute left-1/2 -translate-x-1/2 top-full w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-gray-900"></div>
                                                        </div>
                                                    </div>
                                                );
                                            };

                                            return (
                                                <div className="space-y-3">
                                                    {/* Professional categories section - subtype-aware */}
                                                    {professionalCats.length > 0 && (
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide whitespace-nowrap">
                                                                {getProfessionalLabel()}
                                                            </span>
                                                            <div className="h-px bg-gray-200 w-4"></div>
                                                            <div className="flex flex-wrap items-center gap-1.5">
                                                                {professionalCats.map((cat, idx) => renderCategoryBadge(cat, idx, true))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Standard categories section - collapsible */}
                                                    {standardCats.length > 0 && (
                                                        <details>
                                                            <summary className="cursor-pointer list-none flex items-center gap-2 text-xs font-medium text-gray-400 uppercase tracking-wide whitespace-nowrap hover:text-gray-600">
                                                                <svg className="w-3 h-3 text-gray-400 transition-transform duration-200" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ transform: 'rotate(0deg)' }}>
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                                                </svg>
                                                                Standard: {standardCats.length} categories
                                                            </summary>
                                                            <div className="flex flex-wrap items-center gap-1.5 mt-2 ml-5">
                                                                {standardCats.map((cat, idx) => renderCategoryBadge(cat, idx, false))}
                                                            </div>
                                                        </details>
                                                    )}
                                                </div>
                                            );
                                        })()
                                    ) : (
                                        <span className="text-gray-500 text-sm">None specified</span>
                                    )}
                                </div>
                                </div>
                            </div>

                            {/* Risk Assessment */}
                            <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
                                {/* Header */}
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-base sm:text-lg font-semibold text-gray-900 flex items-center">
                                        <Activity className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-gray-600 flex-shrink-0" />
                                        Risk Assessment
                                    </h3>
                                    {getRiskBadge(checkResult.riskAssessment.level)}
                                </div>

                                {/* Score bar */}
                                <div className="mb-4">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-sm text-gray-600">Risk Score</span>
                                        <span className={`text-sm font-semibold ${
                                            checkResult.riskAssessment.level === 'high' ? 'text-red-600' :
                                            checkResult.riskAssessment.level === 'medium' ? 'text-yellow-600' : 'text-green-600'
                                        }`}>
                                            {Math.min(checkResult.riskAssessment.score, 100)}/100
                                        </span>
                                    </div>
                                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all ${
                                                checkResult.riskAssessment.level === 'high' ? 'bg-red-500' :
                                                checkResult.riskAssessment.level === 'medium' ? 'bg-yellow-500' : 'bg-green-500'
                                            }`}
                                            style={{ width: `${Math.min(checkResult.riskAssessment.score, 100)}%` }}
                                        />
                                    </div>
                                    <div className="text-xs text-gray-500 mt-1">
                                        Next check due: {new Date(checkResult.riskAssessment.nextCheckDue).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </div>
                                </div>

                                {/* Risk Factors - always visible */}
                                {/* Risk Factors - collapsible */}
                                {checkResult.riskAssessment.factors.length > 0 && (
                                    <details className="group mt-3">
                                        <summary className="cursor-pointer list-none py-2 px-3 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100">
                                            <div className="flex items-center text-sm">
                                                <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                                <AlertTriangle className="w-4 h-4 text-yellow-500 mr-1.5 flex-shrink-0" />
                                                <span className="font-medium text-gray-700">
                                                    {checkResult.riskAssessment.factors.length} risk factor{checkResult.riskAssessment.factors.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </summary>
                                        <div className="mt-2 pl-3 border-l-2 border-yellow-200">
                                            <ul className="space-y-1.5">
                                                {checkResult.riskAssessment.factors.map((factor, index) => {
                                                    // Support both string and RiskFactor formats
                                                    const isObject = typeof factor === 'object' && factor !== null;
                                                    const factorObj = isObject ? factor as { text: string; score: number; severity: string } : null;
                                                    const text = factorObj ? factorObj.text : (factor as string);
                                                    const severity = factorObj ? factorObj.severity : (text.startsWith('CRITICAL:') ? 'critical' : text.startsWith('URGENT:') ? 'urgent' : 'warning');
                                                    const scoreValue = factorObj ? factorObj.score : 0;

                                                    // Clean up cryptic restriction codes
                                                    let displayText = text;
                                                    if (text.includes('Professional category restrictions:')) {
                                                        displayText = 'Professional categories have restrictions - see details below';
                                                    }
                                                    // Remove prefixes if present (they're now shown via styling)
                                                    displayText = displayText.replace(/^(CRITICAL|URGENT):\s*/i, '');

                                                    const isCritical = severity === 'critical';
                                                    const isUrgent = severity === 'urgent';
                                                    const isInfo = severity === 'info';

                                                    return (
                                                        <li key={index} className={`text-sm flex items-start space-x-2 ${
                                                            isCritical ? 'text-red-700 font-semibold' :
                                                            isUrgent ? 'text-orange-600 font-medium' :
                                                            isInfo ? 'text-gray-500' :
                                                            'text-gray-600'
                                                        }`}>
                                                            <AlertTriangle className={`w-3 h-3 mt-0.5 flex-shrink-0 ${
                                                                isCritical ? 'text-red-500' :
                                                                isUrgent ? 'text-orange-500' :
                                                                isInfo ? 'text-gray-400' :
                                                                'text-yellow-500'
                                                            }`} />
                                                            <span className="break-words flex-1">
                                                                {isCritical && <span className="font-bold">CRITICAL: </span>}
                                                                {isUrgent && <span className="font-semibold">URGENT: </span>}
                                                                {displayText}
                                                            </span>
                                                            {scoreValue > 0 && (
                                                                <span className={`text-xs px-1.5 py-0.5 rounded flex-shrink-0 ${
                                                                    isCritical ? 'bg-red-100 text-red-700' :
                                                                    isUrgent ? 'bg-orange-100 text-orange-700' :
                                                                    'bg-yellow-100 text-yellow-700'
                                                                }`}>
                                                                    +{scoreValue}
                                                                </span>
                                                            )}
                                                        </li>
                                                    );
                                                })}
                                            </ul>
                                        </div>
                                    </details>
                                )}

                                {/* Recommendations - collapsible */}
                                {checkResult.riskAssessment.recommendations.length > 0 && (
                                    <details className="group mt-3">
                                        <summary className="cursor-pointer list-none py-2 px-3 bg-green-50 border border-green-200 rounded-lg hover:bg-green-100">
                                            <div className="flex items-center text-sm">
                                                <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                    <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </div>
                                                <CheckCircle className="w-4 h-4 mr-1.5" style={{ color: '#69BE28' }} />
                                                <span className="font-medium text-gray-700">
                                                    {checkResult.riskAssessment.recommendations.length} recommendation{checkResult.riskAssessment.recommendations.length !== 1 ? 's' : ''}
                                                </span>
                                            </div>
                                        </summary>
                                        <div className="mt-2 pl-3 border-l-2 border-green-200">
                                            <ul className="space-y-1.5">
                                                {checkResult.riskAssessment.recommendations.map((rec, index) => (
                                                    <li key={index} className="text-sm flex items-start space-x-2" style={{ color: '#69BE28' }}>
                                                        <CheckCircle className="w-3 h-3 mt-0.5 flex-shrink-0" style={{ color: '#69BE28' }} />
                                                        <span className="break-words">{rec}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </details>
                                )}
                            </div>

                            {/* Professional Driver Details */}
                            {checkResult && (checkResult.tachographDetails || checkResult.cpcDetails) && (
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <details className="group">
                                        <summary className="cursor-pointer list-none">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                                                    <CreditCard className="w-5 h-5 mr-2 text-gray-600" />
                                                    Professional Driver Details
                                                </h3>
                                                <div className="flex items-center space-x-2">
                                                <span className="text-sm text-gray-500">
                                                    {/* Subtype-aware summary: bus=CPC only, coach/haulage=CPC+tacho */}
                                                    {subtype === 'bus' ? (
                                                        checkResult.cpcDetails?.length ? 'CPC on record' : 'No CPC data'
                                                    ) : (subtype === 'coach' || subtype === 'haulage') ? (
                                                        `${checkResult.cpcDetails?.length ? 'CPC' : 'No CPC'}${checkResult.tachographDetails?.length ? ` • ${checkResult.tachographDetails.length} tacho` : ' • No tacho'}`
                                                    ) : (
                                                        `${checkResult.cpcDetails?.length ? 'CPC' : ''}${checkResult.cpcDetails?.length && checkResult.tachographDetails?.length ? ' • ' : ''}${checkResult.tachographDetails?.length ? `${checkResult.tachographDetails.length} tacho` : ''}`
                                                    ) || 'No professional data'}
                                                </span>
                                                    <div className="transform transition-transform group-open:rotate-180">
                                                        <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                        </svg>
                                                    </div>
                                                </div>
                                            </div>
                                        </summary>

                                        <div className="mt-4 space-y-4 pl-7">
                                            {/* Tachograph Cards */}
                                            {checkResult.tachographDetails && checkResult.tachographDetails.length > 0 && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                        <CreditCard className="w-4 h-4 mr-2" />
                                                        Tachograph Cards
                                                    </h4>
                                                    <div className="space-y-3">
                                                        {checkResult.tachographDetails.map((card: any, index: number) => (
                                                            <div key={index} className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            Card Number
                                                                        </label>
                                                                        <div className="mt-1 font-mono text-sm font-medium text-gray-900">
                                                                            {card.cardNumber}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            Status
                                                                        </label>
                                                                        <div className="mt-1">
                                                                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${card.cardStatus === 'VALID' || card.cardStatus === 'DISPATCHED' || card.cardStatus === 'RENEWED'
                                                                                    ? 'bg-green-100 text-green-800'
                                                                                    : card.cardStatus === 'EXPIRED'
                                                                                        ? 'bg-red-100 text-red-800'
                                                                                        : 'bg-yellow-100 text-yellow-800'
                                                                                }`}>
                                                                                {card.cardStatus}
                                                                            </span>
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            Valid From
                                                                        </label>
                                                                        <div className="mt-1 text-sm text-gray-900">
                                                                            {new Date(card.cardStartOfValidityDate).toLocaleDateString('en-GB', {
                                                                                day: '2-digit',
                                                                                month: 'short',
                                                                                year: 'numeric'
                                                                            })}
                                                                        </div>
                                                                    </div>

                                                                    <div>
                                                                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                            Expires
                                                                        </label>
                                                                        <div className="mt-1 text-sm">
                                                                            <span className={`${new Date(card.cardExpiryDate) < new Date()
                                                                                    ? 'text-red-600 font-medium'
                                                                                    : new Date(card.cardExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000)
                                                                                        ? 'text-yellow-600 font-medium'
                                                                                        : 'text-gray-900'
                                                                                }`}>
                                                                                {new Date(card.cardExpiryDate).toLocaleDateString('en-GB', {
                                                                                    day: '2-digit',
                                                                                    month: 'short',
                                                                                    year: 'numeric'
                                                                                })}
                                                                                {new Date(card.cardExpiryDate) < new Date() && (
                                                                                    <span className="ml-2 text-xs">(EXPIRED)</span>
                                                                                )}
                                                                                {new Date(card.cardExpiryDate) >= new Date() &&
                                                                                    new Date(card.cardExpiryDate) < new Date(Date.now() + 90 * 24 * 60 * 60 * 1000) && (
                                                                                        <span className="ml-2 text-xs">(EXPIRES SOON)</span>
                                                                                    )}
                                                                            </span>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </div>
                                            )}

                                            {/* CPC Details - subtype-aware (not needed for car_fleet) */}
                                            {subtype !== 'car_fleet' && checkResult.cpcDetails && (
                                                <div>
                                                    <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                        <Calendar className="w-4 h-4 mr-2" />
                                                        {subtype === 'bus' || subtype === 'coach' ? 'Driver CPC (Passenger)' :
                                                         subtype === 'haulage' ? 'Driver CPC (Goods)' :
                                                         'Certificate of Professional Competence (CPC)'}
                                                    </h4>
                                                    <div className="border rounded-lg p-4" style={{
                                                        backgroundColor: 'rgba(105, 190, 40, 0.1)',
                                                        borderColor: 'rgba(105, 190, 40, 0.3)'
                                                    }}>
                                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                                            {checkResult.cpcDetails.map((cpc: any, index: number) => (
                                                                <React.Fragment key={index}>
                                                                    {/* LGV CPC - show for haulage and general only */}
                                                                    {cpc.lgvValidTo && (subtype === 'haulage' || subtype === 'general') && (
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                                LGV (Goods) Valid To
                                                                            </label>
                                                                            <div className="mt-1 text-sm text-gray-900">
                                                                                {new Date(cpc.lgvValidTo).toLocaleDateString('en-GB')}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                    {/* PCV CPC - show for bus/coach and general only */}
                                                                    {cpc.pcvValidTo && (subtype === 'bus' || subtype === 'coach' || subtype === 'general') && (
                                                                        <div>
                                                                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide">
                                                                                PCV (Passenger) Valid To
                                                                            </label>
                                                                            <div className="mt-1 text-sm text-gray-900">
                                                                                {new Date(cpc.pcvValidTo).toLocaleDateString('en-GB')}
                                                                            </div>
                                                                        </div>
                                                                    )}
                                                                </React.Fragment>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Professional Categories - Clean Table (subtype-aware) */}
                                            {subtype !== 'car_fleet' && (
                                            <div>
                                                <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
                                                    <Shield className="w-4 h-4 mr-2" />
                                                    {subtype === 'bus' || subtype === 'coach' ? 'PCV Category Details' :
                                                     subtype === 'haulage' ? 'HGV Category Details' :
                                                     'Professional Category Details'}
                                                </h4>
                                                {(() => {
                                                    const now = new Date();
                                                    // Filter by subtype
                                                    const pcvCodes = ['D', 'D1', 'DE', 'D1E'];
                                                    const hgvCodes = ['C', 'C1', 'CE', 'C1E'];
                                                    const relevantCodes = subtype === 'bus' || subtype === 'coach' ? pcvCodes :
                                                                         subtype === 'haulage' ? hgvCodes :
                                                                         [...pcvCodes, ...hgvCodes];
                                                    const profCats = checkResult.categories.filter(cat =>
                                                        relevantCodes.includes(cat.categoryCode)
                                                    );

                                                    if (profCats.length === 0) {
                                                        return (
                                                            <div className="text-sm text-gray-500 italic">
                                                                {subtype === 'bus' || subtype === 'coach' ? 'No PCV categories (D/D1) found on licence' :
                                                                 subtype === 'haulage' ? 'No HGV categories (C/CE) found on licence' :
                                                                 'No professional driving categories found'}
                                                            </div>
                                                        );
                                                    }

                                                    return (
                                                        <div className="border rounded-lg overflow-hidden">
                                                            <table className="w-full text-sm">
                                                                <thead className="bg-gray-50 border-b">
                                                                    <tr>
                                                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Category</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Valid To</th>
                                                                        <th className="px-3 py-2 text-left font-medium text-gray-600">Restrictions</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody className="divide-y">
                                                                    {profCats.map((cat, index) => {
                                                                        const isExpired = cat.expiryDate && new Date(cat.expiryDate) <= now;
                                                                        const isProvisional = cat.categoryType?.toLowerCase() === 'provisional';
                                                                        const hasRestrictions = cat.restrictions && cat.restrictions.length > 0;

                                                                        let statusBadge;
                                                                        if (isExpired) {
                                                                            statusBadge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700"><XCircle className="w-3 h-3" /> Expired</span>;
                                                                        } else if (isProvisional) {
                                                                            statusBadge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-700"><AlertTriangle className="w-3 h-3" /> Provisional</span>;
                                                                        } else if (hasRestrictions) {
                                                                            statusBadge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700"><AlertCircle className="w-3 h-3" /> Restricted</span>;
                                                                        } else {
                                                                            statusBadge = <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700"><CheckCircle2 className="w-3 h-3" /> Full</span>;
                                                                        }

                                                                        return (
                                                                            <tr key={index} className={isExpired ? 'bg-red-50' : ''}>
                                                                                <td className="px-3 py-2 font-semibold">{cat.categoryCode}</td>
                                                                                <td className="px-3 py-2">{statusBadge}</td>
                                                                                <td className="px-3 py-2 text-gray-600">
                                                                                    {cat.expiryDate ? new Date(cat.expiryDate).toLocaleDateString('en-GB') : '-'}
                                                                                </td>
                                                                                <td className="px-3 py-2">
                                                                                    {hasRestrictions && cat.restrictions ? (
                                                                                        <div className="space-y-0.5">
                                                                                            {cat.restrictions.map((r: any, i: number) => (
                                                                                                <div key={i} className="text-xs text-orange-700">
                                                                                                    <span className="font-medium">{r.restrictionCode}</span>
                                                                                                    {r.restrictionLiteral && <span className="text-gray-500"> - {r.restrictionLiteral}</span>}
                                                                                                </div>
                                                                                            ))}
                                                                                        </div>
                                                                                    ) : (
                                                                                        <span className="text-gray-400">None</span>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    );
                                                })()}
                                            </div>
                                            )}

                                            {/* Tachograph Card Status - required for haulage and coach, some bus operations */}
                                            {subtype !== 'car_fleet' && checkResult.tachographDetails?.length > 0 && (
                                                <div className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-3">
                                                    <span className="text-sm font-medium text-gray-700">Digital Tachograph Card</span>
                                                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                                                        checkResult.tachographDetails?.some((card: any) =>
                                                            ['VALID', 'DISPATCHED', 'RENEWED'].includes(card.cardStatus)
                                                        ) ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                                                    }`}>
                                                        {checkResult.tachographDetails?.some((card: any) =>
                                                            ['VALID', 'DISPATCHED', 'RENEWED'].includes(card.cardStatus)
                                                        ) ? <CheckCircle2 className="w-3 h-3" /> : <XCircle className="w-3 h-3" />}
                                                        {checkResult.tachographDetails[0].cardStatus}
                                                    </span>
                                                </div>
                                            )}
                                        </div>
                                    </details>
                                </div>
                            )}

                            {/* Endorsements - Fleet-friendly view */}
                            {checkResult.endorsements.length > 0 && (() => {
                                const now = new Date();
                                now.setHours(0, 0, 0, 0);

                                // Check if driver is disqualified
                                const isDisqualified = checkResult.status?.toLowerCase().includes('disqualified') ||
                                    checkResult.status?.toLowerCase().includes('revoked');

                                // Helper: check if points are expired
                                const isPointsExpired = (e: typeof checkResult.endorsements[0]) => {
                                    if (!e.penaltyPointsExpiryDate) return false;
                                    const expiryDate = new Date(e.penaltyPointsExpiryDate);
                                    expiryDate.setHours(0, 0, 0, 0);
                                    return expiryDate < now;
                                };

                                // Helper: check if offence is serious (affects insurance)
                                const isSeriousOffence = (code: string) => {
                                    if (!code) return false;
                                    const seriousPrefixes = ['DR', 'DD', 'CD', 'UT', 'BA', 'IN10'];
                                    return seriousPrefixes.some(prefix => code.toUpperCase().startsWith(prefix));
                                };

                                // Calculate active points (unexpired points only)
                                const activePoints = checkResult.endorsements.reduce((sum, e) => {
                                    if (!e.penaltyPoints || isPointsExpired(e)) return sum;
                                    return sum + e.penaltyPoints;
                                }, 0);

                                // Sort endorsements by conviction date (newest first)
                                const sortedEndorsements = [...checkResult.endorsements].sort((a, b) =>
                                    new Date(b.convictionDate).getTime() - new Date(a.convictionDate).getTime()
                                );

                                // Check for serious offences in history
                                const hasSeriousOffence = sortedEndorsements.some(e => isSeriousOffence(e.offenceCode));

                                // Check for active disqualifications in endorsements
                                const hasActiveDisqualification = sortedEndorsements.some(e => {
                                    if (!e.disqualification?.startDate) return false;
                                    const startDate = new Date(e.disqualification.startDate);
                                    const years = e.disqualification.years || 0;
                                    const months = e.disqualification.months || 0;
                                    const endDate = new Date(startDate);
                                    endDate.setFullYear(endDate.getFullYear() + years);
                                    endDate.setMonth(endDate.getMonth() + months);
                                    return endDate > now;
                                });

                                // Points until ban
                                const pointsUntilBan = Math.max(0, 12 - activePoints);
                                const pointsPercentage = Math.min(100, (activePoints / 12) * 100);

                                // For disqualified drivers, show simplified collapsible card
                                if (isDisqualified || hasActiveDisqualification) {
                                    return (
                                        <details className="group bg-white border border-gray-200 rounded-lg overflow-hidden">
                                            <summary className="cursor-pointer list-none p-4 hover:bg-gray-50">
                                                <div className="flex items-center justify-between">
                                                    <div className="flex items-center text-sm">
                                                        <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                        <h4 className="font-medium text-gray-700">
                                                            Driving Record ({sortedEndorsements.length} endorsement{sortedEndorsements.length !== 1 ? 's' : ''})
                                                        </h4>
                                                    </div>
                                                    {hasSeriousOffence && (
                                                        <span className="px-2 py-1 text-xs font-medium bg-amber-100 text-amber-700 rounded">
                                                            Serious offence
                                                        </span>
                                                    )}
                                                </div>
                                            </summary>
                                            <div className="px-4 pb-4 space-y-3">
                                                {sortedEndorsements.map((endorsement, index) => {
                                                    const expired = isPointsExpired(endorsement);
                                                    const serious = isSeriousOffence(endorsement.offenceCode);
                                                    const hasPoints = endorsement.penaltyPoints && endorsement.penaltyPoints > 0;

                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`border rounded-lg p-4 ${
                                                                expired || !hasPoints
                                                                    ? 'bg-gray-50 border-gray-200'
                                                                    : 'bg-orange-50 border-orange-200'
                                                            }`}
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className={`font-semibold ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-900'}`}>
                                                                            {endorsement.offenceCode}
                                                                        </span>
                                                                        {serious && (
                                                                            <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                                                                                Serious
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                    <p className={`text-sm mt-1 ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-600'}`}>
                                                                        {endorsement.offenceLegalLiteral}
                                                                    </p>
                                                                    <p className="text-xs text-gray-400 mt-2">
                                                                        {new Date(endorsement.convictionDate).toLocaleDateString('en-GB', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            year: 'numeric'
                                                                        })}
                                                                    </p>
                                                                </div>
                                                                {hasPoints && (
                                                                    <div className="text-right flex-shrink-0">
                                                                        <span className={`text-lg font-bold ${expired ? 'text-gray-400' : 'text-orange-600'}`}>
                                                                            {endorsement.penaltyPoints}
                                                                        </span>
                                                                        <span className="text-xs text-gray-400 block">pts</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </details>
                                    );
                                }

                                // For non-disqualified drivers, show full Penalty Points card
                                return (
                                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                                        {/* Alert banner for high risk */}
                                        {activePoints >= 9 && (
                                            <div className="bg-red-600 text-white px-4 py-3 flex items-center gap-2">
                                                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                                                <span className="font-medium">
                                                    {activePoints >= 12
                                                        ? 'At or over 12 points - driver may face disqualification'
                                                        : `Warning: ${pointsUntilBan} points until automatic ban`
                                                    }
                                                </span>
                                            </div>
                                        )}

                                        <div className="p-6">
                                            {/* Header with points progress */}
                                            <div className="mb-6">
                                                <div className="flex items-center justify-between mb-2">
                                                    <h3 className="text-lg font-semibold text-gray-900">
                                                        Penalty Points
                                                    </h3>
                                                    <div className="text-right">
                                                        <span className={`text-2xl font-bold ${
                                                            activePoints >= 9 ? 'text-red-600' :
                                                            activePoints >= 6 ? 'text-orange-500' :
                                                            'text-green-600'
                                                        }`}>
                                                            {activePoints}
                                                        </span>
                                                        <span className="text-gray-400 text-lg">/12</span>
                                                    </div>
                                                </div>

                                                {/* Progress bar */}
                                                <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                                                    <div
                                                        className={`h-full transition-all duration-300 ${
                                                            activePoints >= 9 ? 'bg-red-500' :
                                                            activePoints >= 6 ? 'bg-orange-400' :
                                                            'bg-green-500'
                                                        }`}
                                                        style={{ width: `${pointsPercentage}%` }}
                                                    />
                                                </div>
                                                <p className="text-sm text-gray-500 mt-1">
                                                    {activePoints === 0
                                                        ? (hasSeriousOffence
                                                            ? 'No active penalty points - but has offence history'
                                                            : 'No active penalty points')
                                                        : `${pointsUntilBan} points until automatic disqualification`
                                                    }
                                                </p>
                                            </div>

                                            {/* Serious offence warning */}
                                            {hasSeriousOffence && (
                                                <div className="mb-4 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2">
                                                    <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                                                    <span className="text-sm text-amber-800">
                                                        Driver has serious offence(s) on record - may affect insurance
                                                    </span>
                                                </div>
                                            )}

                                            {/* Endorsements list - collapsible */}
                                            <details className="group">
                                                <summary className="cursor-pointer list-none">
                                                    <div className="flex items-center text-sm">
                                                        <div className="transform transition-transform group-open:rotate-180 mr-2">
                                                            <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                            </svg>
                                                        </div>
                                                        <h4 className="font-medium text-gray-700">
                                                            Driving Record ({sortedEndorsements.length} endorsement{sortedEndorsements.length !== 1 ? 's' : ''})
                                                        </h4>
                                                    </div>
                                                </summary>

                                                <div className="mt-3 space-y-3">
                                                {sortedEndorsements.map((endorsement, index) => {
                                                    const expired = isPointsExpired(endorsement);
                                                    const serious = isSeriousOffence(endorsement.offenceCode);
                                                    const hasPoints = endorsement.penaltyPoints && endorsement.penaltyPoints > 0;

                                                    return (
                                                        <div
                                                            key={index}
                                                            className={`border rounded-lg p-4 ${
                                                                expired || !hasPoints
                                                                    ? 'bg-gray-50 border-gray-200'
                                                                    : 'bg-orange-50 border-orange-200'
                                                            }`}
                                                        >
                                                            <div className="flex items-start justify-between gap-4">
                                                                <div className="flex-1 min-w-0">
                                                                    {/* Offence code and flags */}
                                                                    <div className="flex items-center gap-2 flex-wrap">
                                                                        <span className={`font-semibold ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-900'}`}>
                                                                            {endorsement.offenceCode}
                                                                        </span>
                                                                        {serious && (
                                                                            <span className="px-1.5 py-0.5 text-xs font-medium bg-red-100 text-red-700 rounded">
                                                                                Serious
                                                                            </span>
                                                                        )}
                                                                        {expired && hasPoints && (
                                                                            <span className="px-1.5 py-0.5 text-xs font-medium bg-gray-200 text-gray-600 rounded">
                                                                                Expired
                                                                            </span>
                                                                        )}
                                                                    </div>

                                                                    {/* Description */}
                                                                    <p className={`text-sm mt-1 ${expired || !hasPoints ? 'text-gray-500' : 'text-gray-600'}`}>
                                                                        {endorsement.offenceLegalLiteral}
                                                                    </p>

                                                                    {/* Date */}
                                                                    <p className="text-xs text-gray-400 mt-2">
                                                                        {new Date(endorsement.convictionDate).toLocaleDateString('en-GB', {
                                                                            day: 'numeric',
                                                                            month: 'short',
                                                                            year: 'numeric'
                                                                        })}
                                                                        {endorsement.penaltyPointsExpiryDate && (
                                                                            <span className="ml-2">
                                                                                · Points {expired ? 'expired' : 'expire'} {new Date(endorsement.penaltyPointsExpiryDate).toLocaleDateString('en-GB', {
                                                                                    day: 'numeric',
                                                                                    month: 'short',
                                                                                    year: 'numeric'
                                                                                })}
                                                                            </span>
                                                                        )}
                                                                    </p>

                                                                    {/* Disqualification */}
                                                                    {endorsement.disqualification && (
                                                                        <div className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
                                                                            <AlertCircle className="w-3.5 h-3.5" />
                                                                            <span>
                                                                                Disqualified
                                                                                {endorsement.disqualification.years && ` ${endorsement.disqualification.years} year${endorsement.disqualification.years !== 1 ? 's' : ''}`}
                                                                                {endorsement.disqualification.months && ` ${endorsement.disqualification.months} month${endorsement.disqualification.months !== 1 ? 's' : ''}`}
                                                                                {endorsement.disqualification.startDate && ` from ${new Date(endorsement.disqualification.startDate).toLocaleDateString('en-GB')}`}
                                                                            </span>
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                {/* Points badge */}
                                                                <div className="flex-shrink-0">
                                                                    {hasPoints ? (
                                                                        <div className={`text-center min-w-[48px] px-2 py-1 rounded-lg ${
                                                                            expired
                                                                                ? 'bg-gray-200 text-gray-500'
                                                                                : 'bg-orange-500 text-white'
                                                                        }`}>
                                                                            <div className={`text-lg font-bold ${expired ? 'line-through' : ''}`}>
                                                                                {endorsement.penaltyPoints}
                                                                            </div>
                                                                            <div className="text-xs opacity-75">pts</div>
                                                                        </div>
                                                                    ) : (
                                                                        <div className="text-center min-w-[48px] px-2 py-1 rounded-lg bg-gray-100 text-gray-400">
                                                                            <div className="text-lg font-bold">-</div>
                                                                            <div className="text-xs">pts</div>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                                </div>
                                            </details>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Restrictions */}
                            {checkResult.restrictions.length > 0 && (
                                <div className="bg-white border border-gray-200 rounded-lg p-6">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                                        <AlertCircle className="w-5 h-5 mr-2 text-gray-600" />
                                        Restrictions
                                    </h3>

                                    <ul className="space-y-2">
                                        {checkResult.restrictions.map((restriction, index) => (
                                            <li key={index} className="text-sm text-gray-700 flex items-center space-x-2">
                                                <AlertCircle className="w-4 h-4 text-red-500" />
                                                <span>{restriction}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                        </div>
                    )}

                    {/* No Previous Check Message */}
                    {!isLoadingLatest && !checkResult && !isChecking && (
                        <div className="text-center py-8">
                            <Shield className="w-12 h-12 mx-auto text-gray-400 mb-4" />
                            <h3 className="text-lg font-medium text-gray-900 mb-2">No Previous Checks</h3>
                            <p className="text-gray-600 mb-4">
                                This driver hasn't had their licence checked yet.
                            </p>
                            {driver.consentProvided && (
                                <p className="text-sm text-gray-500">
                                    Click "Perform New Check" to validate their DVLA licence status.
                                </p>
                            )}
                        </div>
                    )}

                    {checkResult && (
    <div className="px-4 sm:px-6 pb-4">
        <div className="pt-4 border-t border-gray-200">
            <button
                onClick={() => {
                    setShowHistory(!showHistory);
                    if (!showHistory && checkHistory.length === 0) {
                        loadCheckHistory();
                    }
                }}
                disabled={isLoadingHistory}
                className="flex items-center space-x-2 hover:opacity-80 transition-opacity disabled:opacity-50"
                style={{ color: '#69BE28' }}
            >
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span>
                    {isLoadingHistory
                        ? 'Loading...'
                        : showHistory
                            ? 'Hide Check History'
                            : 'View Check History'
                    }
                </span>
            </button>
        </div>

        {/* History Section */}
        {showHistory && (
            <div className="mt-4 space-y-3">
                <h4 className="text-sm font-medium text-gray-700">Previous Checks</h4>

                {isLoadingHistory ? (
                    <div className="text-center py-4">
                        <Loader2 className="w-4 h-4 animate-spin mx-auto" style={{ color: '#69BE28' }} />
                        <p className="text-sm text-gray-600 mt-2">Loading history...</p>
                    </div>
                ) : checkHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 py-4">No previous checks found.</p>
                ) : (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                        {checkHistory.map((check, index) => (
                            <div key={check.id} className="bg-gray-50 rounded-lg p-3">
                                <div className="space-y-2">
                                    <div className="flex items-start justify-between">
                                        <div className="min-w-0 flex-1">
                                            <div className="flex items-center space-x-2">
                                                <div className="font-medium text-sm truncate">
                                                    {formatDate(check.checkDate)}
                                                </div>
                                                {check.isAutomated && (
                                                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                                        <Bot className="w-3 h-3 mr-1" />
                                                        AUTO
                                                    </span>
                                                )}
                                            </div>
                                            {index === 0 && (
                                                <span className="text-xs font-medium" style={{ color: '#69BE28' }}>
                                                    (Current)
                                                </span>
                                            )}
                                        </div>
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        <div className="text-xs">
                                            {getRiskBadge(check.riskAssessment?.level || check.riskLevel)}
                                        </div>
                                        <div className="text-xs">
                                            {getStatusBadge(check.status, check.valid)}
                                        </div>
                                    </div>
                                    <div className="text-xs text-gray-600 space-y-1">
                                        <div>Penalty Points: {(check.status?.toLowerCase().includes('disqualified') ||
                                            check.status?.toLowerCase().includes('revoked'))
                                            ? <span className="text-red-600 font-medium">N/A</span>
                                            : check.penaltyPoints}</div>
                                        <div>Score: {check.riskAssessment?.score || 0}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        )}
    </div>
)}
                </div>

                {/* Footer */}
                <div className="flex items-center justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
                    <button
                        onClick={onClose}
                        disabled={isChecking}
                        className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Recent check confirmation modal */}
            {showRecentCheckConfirm && driver && (
                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center p-4 z-60">
                    <div className="bg-white rounded-lg max-w-md w-full shadow-xl">
                        <div className="p-6">
                            <div className="flex items-start gap-3 mb-4">
                                <div className="p-2 bg-yellow-100 rounded-full flex-shrink-0">
                                    <AlertTriangle className="w-5 h-5 text-yellow-600" />
                                </div>
                                <div>
                                    <h3 className="text-base font-semibold text-gray-900">Driver Recently Checked</h3>
                                    <p className="text-sm text-gray-600 mt-1">
                                        {driver.firstName} {driver.lastName} was last checked on{' '}
                                        <span className="font-medium">{formatDate(driver.lastCheckDate!)}</span>
                                        {' '}({driver.daysSinceLastCheck} {driver.daysSinceLastCheck === 1 ? 'day' : 'days'} ago).
                                    </p>
                                    <p className="text-sm text-gray-600 mt-2">
                                        Running another check so soon may use an additional DVLA credit. Do you want to continue?
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div className="flex gap-3 px-6 pb-6">
                            <button
                                onClick={() => setShowRecentCheckConfirm(false)}
                                className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={performLicenceCheck}
                                className="flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg hover:opacity-90 transition-opacity"
                                style={{ backgroundColor: '#69BE28' }}
                            >
                                Check Anyway
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default LicenceCheckModal;